import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";

// ===== Styled components =====
const ModalBackground = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  background: #fff;
  padding: 24px;
  border-radius: 10px;
  width: 680px;
  max-height: 88vh;
  overflow: auto; /* This causes clipping if tooltip goes outside */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;

const Heading = styled.h2`
  margin: 0 0 12px;
`;

const Subheading = styled.h3`
  font-size: 16px;
  margin: 12px 0 8px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-weight: 600;
  margin: 12px 0 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ButtonBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: ${(p) => (p.variant === "primary" ? "#2f855a" : "#e2e8f0")};
  color: ${(p) => (p.variant === "primary" ? "#fff" : "#111")};
`;

const ProgressOuter = styled.div`
  height: 8px;
  background: #edf2f7;
  border-radius: 999px;
  overflow: hidden;
  margin: 6px 0 16px;
`;

const ProgressInner = styled.div`
  height: 100%;
  width: ${(p) => p.w || 0}%;
  background: #38a169;
`;

const HelpText = styled.p`
  color: #4a5568;
  font-size: 12px;
  margin: 6px 0 0;
  width: 100%;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Chip = styled.button`
  border: 1px solid ${(p) => (p.active ? "#2f855a" : "#cbd5e0")};
  background: ${(p) => (p.active ? "#e6fffa" : "#fff")};
  color: #2d3748;
  padding: 6px 10px;
  border-radius: 999px;
  cursor: pointer;
`;

// ===== Tooltip Components (Modified) =====
const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #718096;
  font-size: 11px;
  font-weight: bold;
  margin-left: 8px;
  cursor: help;
  position: relative;

  &:hover > div {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipContent = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  /* ✅ CHANGED: Position below the icon to avoid top-clipping */
  top: 130%;
  left: 50%;
  transform: translateX(-50%);
  background: #2d3748;
  color: #fff;
  padding: 10px;
  border-radius: 6px;
  font-size: 11px;
  width: 260px;
  text-align: left;
  z-index: 9999;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  font-weight: normal;
  line-height: 1.4;
  transition: opacity 0.2s ease-in-out;
  pointer-events: none;

  /* ✅ CHANGED: Arrow now points up from the top of the tooltip */
  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Place above the tooltip box */
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent #2d3748 transparent; /* Point up */
  }
`;

// Reusable Tooltip Component
const InfoTooltip = ({ descriptions }) => (
  <InfoIconWrapper>
    i
    <TooltipContent>
      {Object.entries(descriptions).map(([key, val]) => (
        <div key={key} style={{ marginBottom: 4, display: "flex", gap: "4px" }}>
          <strong style={{ color: "#a0aec0", whiteSpace: "nowrap" }}>
            {key}:
          </strong>
          <span>{val}</span>
        </div>
      ))}
    </TooltipContent>
  </InfoIconWrapper>
);

// ===== Descriptions Data =====
const PURPOSE_DESCRIPTIONS = {
  Inform: "Deliver facts clearly.",
  Educate: "Teach a concept/skill.",
  "Drive Traffic": "Focus on clicks/views.",
  "Lead Gen": "Encourage sign-ups.",
  "Brand Awareness": "Promote visibility.",
  "Thought Leadership": "Build authority.",
  "SEO-focused": "Target keywords.",
};

const KNOWLEDGE_DESCRIPTIONS = {
  Beginner: "No prior knowledge assumed.",
  Intermediate: "Basic concepts known.",
  Advanced: "Deep dive for pros.",
  Expert: "Highly technical analysis.",
};

const TONE_DESCRIPTIONS = {
  Professional: "Formal, respectful.",
  Conversational: "Chatty, personal.",
  Friendly: "Warm, approachable.",
  Authoritative: "Confident, expert.",
  Casual: "Relaxed, slang allowed.",
  Persuasive: "Convincing, salesy.",
  Neutral: "Unbiased, objective.",
};

// ===== Component =====
export default function GenerateModal({
  isOpen,
  onClose,
  onSubmit,
  initialValues = {}, // Default empty object causing stability issues
  currentUser,
  projects = [],
  defaultProjectId = "",
}) {
  if (!isOpen) return null;

  const [mode, setMode] = useState("choose");
  const [pdfText, setPdfText] = useState("");

  // Step control (1..6 for visible steps)
  const [step, setStep] = useState(0);
  const totalSteps = mode === "pdf" ? 2 : 7;
  const progress = useMemo(
    () => Math.round((step / totalSteps) * 100),
    [step, totalSteps]
  );

  const [selectedProjectId, setSelectedProjectId] = useState(
    defaultProjectId || ""
  );

  // Step 1: General Information
  const [title, setTitle] = useState(initialValues.title || "");
  const PURPOSE_OPTIONS = Object.keys(PURPOSE_DESCRIPTIONS);
  const [purpose, setPurpose] = useState(initialValues.purpose || []);

  // Step 2: Audience & Intent
  const [primaryAudience, setPrimaryAudience] = useState(
    initialValues.primaryAudience || ""
  );
  const [secondaryAudience, setSecondaryAudience] = useState(
    initialValues.secondaryAudience || ""
  );
  const KNOWLEDGE_LEVELS = Object.keys(KNOWLEDGE_DESCRIPTIONS);
  const [audienceKnowledgeLevel, setAudienceKnowledgeLevel] = useState(
    initialValues.audienceKnowledgeLevel || "Beginner"
  );

  // Step 3: Article Details & Specifications
  const LENGTH_OPTIONS = ["<500", "<1000", "<1500", "<2000", "2000+"];
  const [articleLength, setArticleLength] = useState(
    initialValues.articleLength || "<1000"
  );

  const TONE_OPTIONS = Object.keys(TONE_DESCRIPTIONS);
  const [tone, setTone] = useState(initialValues.tone || []);

  // Step 4: SEO & Keywords
  const [primaryKeywords, setPrimaryKeywords] = useState(
    initialValues.primaryKeywordsRaw || initialValues.primaryKeywords || ""
  );
  const [secondaryKeywords, setSecondaryKeywords] = useState(
    initialValues.secondaryKeywordsRaw || initialValues.secondaryKeywords || ""
  );

  // Step 5: Content Structure & Key Points
  const [referenceUrls, setReferenceUrls] = useState(
    initialValues.referenceUrlsRaw || initialValues.referenceUrls || ""
  );
  const [keyMessages, setKeyMessages] = useState(
    initialValues.keyMessagesRaw || initialValues.keyMessages || ""
  );
  const [commonMistakes, setCommonMistakes] = useState(
    initialValues.commonMistakesRaw || initialValues.commonMistakes || ""
  );

  // Step 8: Additional Notes & Comments
  const [additionalNotes, setAdditionalNotes] = useState(
    initialValues.additionalNotes || ""
  );

  // ✅ FIX: Stabilize initialValues using JSON.stringify to prevent infinite re-renders
  const stableInitialValues = useMemo(
    () => initialValues,
    [JSON.stringify(initialValues)]
  );

  // Sync state when stableInitialValues change
  useEffect(() => {
    if (isOpen) {
      if (stableInitialValues.briefMode) setMode(stableInitialValues.briefMode);
      if (stableInitialValues.uploadedBriefText)
        setPdfText(stableInitialValues.uploadedBriefText);

      setTitle(stableInitialValues.title || "");
      setPurpose(stableInitialValues.purpose || []);
      setPrimaryAudience(stableInitialValues.primaryAudience || "");
      setSecondaryAudience(stableInitialValues.secondaryAudience || "");
      setAudienceKnowledgeLevel(
        stableInitialValues.audienceKnowledgeLevel || "Beginner"
      );
      setArticleLength(stableInitialValues.articleLength || "<1000");
      setTone(stableInitialValues.tone || []);

      setPrimaryKeywords(
        stableInitialValues.primaryKeywordsRaw ||
          stableInitialValues.primaryKeywords ||
          ""
      );
      setSecondaryKeywords(
        stableInitialValues.secondaryKeywordsRaw ||
          stableInitialValues.secondaryKeywords ||
          ""
      );
      setReferenceUrls(
        stableInitialValues.referenceUrlsRaw ||
          stableInitialValues.referenceUrls ||
          ""
      );
      setKeyMessages(
        stableInitialValues.keyMessagesRaw ||
          stableInitialValues.keyMessages ||
          ""
      );
      setCommonMistakes(
        stableInitialValues.commonMistakesRaw ||
          stableInitialValues.commonMistakes ||
          ""
      );

      setAdditionalNotes(stableInitialValues.additionalNotes || "");
      setSelectedProjectId(
        stableInitialValues.project_id || defaultProjectId || ""
      );

      // If we have content (editing), jump to step 1. Otherwise start at 0 (Choose Mode)
      if (stableInitialValues.title || stableInitialValues.briefMode) {
        setStep(1);
      } else {
        setStep(0);
      }
    }
  }, [isOpen, stableInitialValues, defaultProjectId]);

  // Helpers
  const parseCSV = (str) =>
    String(str || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const toggleChip = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const handleNext = () => setStep((s) => Math.min(totalSteps, s + 1));
  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = (e) => {
    e?.preventDefault?.();

    const payload = {
      briefMode: mode, // 'wizard' | 'pdf'
      uploadedBriefText: pdfText || "",
      uploaded_assets: initialValues.uploaded_assets || [],
      // Step 1
      title,
      purpose, // array of strings
      // Step 2
      primaryAudience,
      secondaryAudience,
      audienceKnowledgeLevel,
      // Step 3
      articleLength,
      tone, // array of strings
      // Step 4
      primaryKeywordsArray: parseCSV(primaryKeywords),
      secondaryKeywordsArray: parseCSV(secondaryKeywords),
      primaryKeywordsRaw: primaryKeywords,
      secondaryKeywordsRaw: secondaryKeywords,
      // Step 5
      referenceUrlsArray: parseCSV(referenceUrls),
      keyMessagesArray: parseCSV(keyMessages),
      commonMistakesArray: parseCSV(commonMistakes),
      referenceUrlsRaw: referenceUrls,
      keyMessagesRaw: keyMessages,
      commonMistakesRaw: commonMistakes,
      // Step 6
      additionalNotes,
      // Step 7
      project_id: selectedProjectId || null,
    };

    onSubmit?.(payload);
    onClose?.();
  };

  const extractTextFromPdf = async (file) => {
    const [{ getDocument }] = await Promise.all([
      import("pdfjs-dist/build/pdf.mjs"),
      import("pdfjs-dist/build/pdf.worker.min.mjs"),
    ]);
    const reader = new FileReader();
    const arrayBuf = await new Promise((res, rej) => {
      reader.onload = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsArrayBuffer(file);
    });
    const pdf = await getDocument({ data: arrayBuf }).promise;
    let all = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      all += content.items.map((t) => t.str).join(" ") + "\n";
    }
    return all.trim();
  };

  const visibleProjects = useMemo(() => {
    if (!currentUser) return projects;
    if ((currentUser.role || "").toLowerCase() === "admin") return projects;
    // non-admin: include projects where user is a member
    return projects.filter((p) =>
      Array.isArray(p.members) ? p.members.includes(currentUser.id) : true
    );
  }, [projects, currentUser]);

  return (
    <ModalBackground>
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-wizard-title"
      >
        <CloseButton onClick={onClose} aria-label="Close">
          &times;
        </CloseButton>
        <Heading id="article-wizard-title">Generate AI Article</Heading>
        <ProgressOuter>
          <ProgressInner w={progress} />
        </ProgressOuter>

        {/* Step indicator */}
        {step > 0 && (
          <Subheading>
            Step {step} of {totalSteps}
          </Subheading>
        )}

        {/* ====== STEP 0: Choose brief source ====== */}
        {step === 0 && (
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div style={{ borderRight: "1px solid #e2e8f0", paddingRight: 16 }}>
              <h3>Create Custom Brief</h3>
              <p>Use the guided brief builder to generate an article.</p>
              <Button
                variant="primary"
                onClick={() => {
                  setMode("wizard");
                  setStep(1);
                }}
              >
                Start Custom Brief
              </Button>
            </div>
            <div style={{ paddingLeft: 16 }}>
              <h3>Upload PDF Brief</h3>
              <p>Upload an existing brief PDF. We’ll generate from that.</p>
              <input
                type="file"
                accept="application/pdf"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const txt = await extractTextFromPdf(f);
                    setPdfText(txt);
                    setMode("pdf");
                    setStep(1);
                  } catch (err) {
                    alert("Could not read PDF. Please try another file.");
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* ====== WIZARD FLOW: Step 1 ====== */}
        {mode === "wizard" && step === 1 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Label>Article Title / Working Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., The Ultimate Guide to Programmatic SEO"
              required
            />

            <Label style={{ marginTop: 12, flexWrap: "wrap" }}>
              Purpose / Goal of Content
              <InfoTooltip descriptions={PURPOSE_DESCRIPTIONS} />
              <HelpText>Select one or more goals</HelpText>
            </Label>

            <Chips>
              {PURPOSE_OPTIONS.map((opt) => (
                <Chip
                  type="button"
                  key={opt}
                  active={purpose.includes(opt)}
                  onClick={() => setPurpose((p) => toggleChip(p, opt))}
                >
                  {opt}
                </Chip>
              ))}
            </Chips>

            <ButtonBar>
              <span />
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== WIZARD FLOW: Step 2 ====== */}
        {mode === "wizard" && step === 2 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Row>
              <div>
                <Label>Primary Audience</Label>
                <Input
                  value={primaryAudience}
                  onChange={(e) => setPrimaryAudience(e.target.value)}
                  placeholder="e.g., Marketing Managers at SaaS startups"
                />
              </div>
              <div>
                <Label>Secondary Audience</Label>
                <Input
                  value={secondaryAudience}
                  onChange={(e) => setSecondaryAudience(e.target.value)}
                  placeholder="e.g., Content Writers, SEO Specialists"
                />
              </div>
            </Row>

            <Label style={{ marginTop: 12 }}>
              Audience Knowledge Level
              <InfoTooltip descriptions={KNOWLEDGE_DESCRIPTIONS} />
            </Label>
            <Select
              value={audienceKnowledgeLevel}
              onChange={(e) => setAudienceKnowledgeLevel(e.target.value)}
            >
              {KNOWLEDGE_LEVELS.map((lvl) => (
                <option key={lvl} value={lvl}>
                  {lvl}
                </option>
              ))}
            </Select>

            <ButtonBar>
              <Button onClick={handleBack}>Back</Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== WIZARD FLOW: Step 3 ====== */}
        {mode === "wizard" && step === 3 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Row>
              <div>
                <Label>Article Length (word count)</Label>
                <Select
                  value={articleLength}
                  onChange={(e) => setArticleLength(e.target.value)}
                >
                  {LENGTH_OPTIONS.map((len) => (
                    <option key={len} value={len}>
                      {len}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>
                  Tone & Voice
                  <InfoTooltip descriptions={TONE_DESCRIPTIONS} />
                </Label>
                <HelpText style={{ width: "100%" }}>
                  Select one or more tones
                </HelpText>
                <Chips>
                  {TONE_OPTIONS.map((opt) => (
                    <Chip
                      type="button"
                      key={opt}
                      active={tone.includes(opt)}
                      onClick={() => setTone((t) => toggleChip(t, opt))}
                    >
                      {opt}
                    </Chip>
                  ))}
                </Chips>
              </div>
            </Row>

            <ButtonBar>
              <Button onClick={handleBack}>Back</Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== WIZARD FLOW: Step 4 ====== */}
        {mode === "wizard" && step === 4 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Label style={{ flexWrap: "wrap" }}>
              Primary Keyword(s){" "}
              <HelpText>Primary SEO Keywords (comma-separated)</HelpText>
            </Label>
            <Input
              value={primaryKeywords}
              onChange={(e) => setPrimaryKeywords(e.target.value)}
              placeholder="e.g., programmatic SEO, automated content"
            />

            <Label style={{ marginTop: 12, flexWrap: "wrap" }}>
              Secondary Keywords{" "}
              <HelpText>Secondary SEO Keywords (comma-separated)</HelpText>
            </Label>
            <Input
              value={secondaryKeywords}
              onChange={(e) => setSecondaryKeywords(e.target.value)}
              placeholder="e.g., internal linking, topic clusters"
            />

            <ButtonBar>
              <Button onClick={handleBack}>Back</Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== WIZARD FLOW: Step 5 ====== */}
        {mode === "wizard" && step === 5 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Label style={{ flexWrap: "wrap" }}>
              Reference URLs{" "}
              <HelpText>
                Add URLs to Articles You Would Like to Model (comma-separated)
              </HelpText>
            </Label>
            <TextArea
              rows={3}
              value={referenceUrls}
              onChange={(e) => setReferenceUrls(e.target.value)}
              placeholder="e.g., https://example.com/a, https://example.com/b"
            />

            <Label style={{ flexWrap: "wrap" }}>
              Key Messages / Takeaways{" "}
              <HelpText>
                What Key Points Would You Like Mentioned in the Article
                (comma-separated)
              </HelpText>
            </Label>
            <TextArea
              rows={3}
              value={keyMessages}
              onChange={(e) => setKeyMessages(e.target.value)}
              placeholder="e.g., Why it matters, How it works, ROI proof"
            />

            <Label style={{ flexWrap: "wrap" }}>
              Article Constraints / Customization (Whitelist/Blacklist & Rules){" "}
              <HelpText>
                Create Rules to Further Customize the Article (comma-separated)
              </HelpText>
            </Label>
            <TextArea
              rows={3}
              value={commonMistakes}
              onChange={(e) => setCommonMistakes(e.target.value)}
              placeholder="e.g., Avoid keyword stuffing, No outdated stats, Cite sources"
            />

            <ButtonBar>
              <Button onClick={handleBack}>Back</Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== WIZARD FLOW: Step 6 ====== */}
        {mode === "wizard" && step === 6 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Label>Anything Else We Should Know?</Label>
            <TextArea
              rows={6}
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Add constraints, style guidelines, further customization, etc."
            />

            <ButtonBar>
              <Button onClick={handleBack}>Back</Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== WIZARD FLOW: Step 7 ====== */}
        {mode === "wizard" && step === 7 && (
          <form onSubmit={handleSubmit}>
            <Label>Assign to Project</Label>
            <HelpText>
              Choose which project this new article should belong to.
            </HelpText>
            <Select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              <option value="">— No Project —</option>
              {visibleProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || "Untitled Project"}
                </option>
              ))}
            </Select>

            <ButtonBar>
              <Button onClick={handleBack}>Back</Button>
              <Button type="submit" variant="primary">
                Save & Generate
              </Button>
            </ButtonBar>
          </form>
        )}
        {/* ====== PDF FLOW: Step 1 (preview & optional notes) ====== */}
        {mode === "pdf" && step === 1 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            <Label>Article Title / Working Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., The Ultimate Guide to Programmatic SEO"
              required
            />

            <Label style={{ marginTop: 12 }}>Extracted Brief (read-only)</Label>
            <TextArea rows={10} value={pdfText} readOnly />

            <Label style={{ marginTop: 12 }}>Additional Notes (optional)</Label>
            <TextArea
              rows={6}
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Add constraints, style guidelines, or clarifications here"
            />

            <ButtonBar>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </ButtonBar>
          </form>
        )}

        {/* ====== PDF FLOW: Step 2 (assign project & submit) ====== */}
        {mode === "pdf" && step === 2 && (
          <form onSubmit={handleSubmit}>
            <Label>Assign to Project</Label>
            <Select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              <option value="">— No Project —</option>
              {visibleProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || "Untitled Project"}
                </option>
              ))}
            </Select>

            <ButtonBar>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button type="submit" variant="primary">
                Save & Generate
              </Button>
            </ButtonBar>
          </form>
        )}
      </ModalContent>
    </ModalBackground>
  );
}
